// src/types/coordinateGrid.ts
/**
 * Type definitions for the 2D Coordinate Grid feature
 * Phase 1: Core infrastructure types
 */

/**
 * Basic 2D point representation
 */
export interface Point2D {
  x: number;
  y: number;
}

/**
 * Grid visual style options
 */
export type GridStyle = 'lines' | 'dots' | 'none';

/**
 * Grid density presets
 * - coarse: gridlines at integer intervals only
 * - normal: gridlines at 0.5 intervals
 * - fine: gridlines at 0.25 intervals
 */
export type GridDensity = 'coarse' | 'normal' | 'fine';

/**
 * Label format for axis tick labels
 */
export type AxisLabelFormat = 'integer' | 'decimal' | 'fraction';

/**
 * Quadrant visibility configuration
 * Each quadrant can be independently shown/hidden
 */
export interface QuadrantVisibility {
  /** Quadrant 1: +x, +y (top-right) */
  q1: boolean;
  /** Quadrant 2: -x, +y (top-left) */
  q2: boolean;
  /** Quadrant 3: -x, -y (bottom-left) */
  q3: boolean;
  /** Quadrant 4: +x, -y (bottom-right) */
  q4: boolean;
}

/**
 * Axis configuration for X and Y axes
 */
export interface AxisSettings {
  /** Show the X-axis line */
  showXAxis: boolean;
  /** Show the Y-axis line */
  showYAxis: boolean;
  /** Label for the X-axis (displayed at the end of the axis) */
  xLabel: string;
  /** Label for the Y-axis (displayed at the end of the axis) */
  yLabel: string;
  /** Show arrow heads at the positive ends of axes */
  showArrows: boolean;
  /** Interval between major tick marks */
  tickInterval: number;
  /** Format for tick labels */
  labelFormat: AxisLabelFormat;
  /** Show tick labels on X-axis */
  showXLabels: boolean;
  /** Show tick labels on Y-axis */
  showYLabels: boolean;
}

/**
 * Main coordinate grid state
 */
export interface CoordinateGridState {
  /** Whether to show the background grid */
  showGrid: boolean;
  /** Visual style of the grid (lines, dots, or none) */
  gridStyle: GridStyle;
  /** Density of gridlines */
  gridDensity: GridDensity;
  /** Color of the grid lines/dots */
  gridColor: string;
  /** Whether to show minor gridlines between major gridlines */
  showMinorGridlines: boolean;
  /** Minor gridline color (typically lighter than main grid) */
  minorGridColor: string;
  /** Axis display and labeling settings */
  axisSettings: AxisSettings;
  /** Which quadrants are visible */
  quadrantsVisible: QuadrantVisibility;
  /** Lock X and Y scales to be equal (maintains square grid cells) */
  equalScaling: boolean;
  /** Axis line color */
  axisColor: string;
  /** Axis line thickness */
  axisThickness: number;
  /** Origin marker visibility */
  showOriginMarker: boolean;
}

/**
 * Default axis settings
 */
export const DEFAULT_AXIS_SETTINGS: AxisSettings = {
  showXAxis: true,
  showYAxis: true,
  xLabel: 'x',
  yLabel: 'y',
  showArrows: true,
  tickInterval: 1,
  labelFormat: 'integer',
  showXLabels: true,
  showYLabels: true,
};

/**
 * Default quadrant visibility (all visible)
 */
export const DEFAULT_QUADRANT_VISIBILITY: QuadrantVisibility = {
  q1: true,
  q2: true,
  q3: true,
  q4: true,
};

/**
 * Default coordinate grid state
 */
export const DEFAULT_COORDINATE_GRID_STATE: CoordinateGridState = {
  showGrid: true,
  gridStyle: 'lines',
  gridDensity: 'normal',
  gridColor: '#E5E7EB',
  showMinorGridlines: false,
  minorGridColor: '#F3F4F6',
  axisSettings: DEFAULT_AXIS_SETTINGS,
  quadrantsVisible: DEFAULT_QUADRANT_VISIBILITY,
  equalScaling: true,
  axisColor: '#374151',
  axisThickness: 2,
  showOriginMarker: true,
};

/**
 * Quadrant presets for quick selection
 */
export type QuadrantPreset = 
  | 'all'           // All four quadrants
  | 'q1-only'       // First quadrant only (positive x and y)
  | 'q1-q4'         // Right half (positive x)
  | 'q1-q2'         // Top half (positive y)
  | 'custom';       // Custom selection

/**
 * Get quadrant visibility from preset
 */
export function getQuadrantPreset(preset: QuadrantPreset): QuadrantVisibility {
  switch (preset) {
    case 'all':
      return { q1: true, q2: true, q3: true, q4: true };
    case 'q1-only':
      return { q1: true, q2: false, q3: false, q4: false };
    case 'q1-q4':
      return { q1: true, q2: false, q3: false, q4: true };
    case 'q1-q2':
      return { q1: true, q2: true, q3: false, q4: false };
    case 'custom':
    default:
      return { q1: true, q2: true, q3: true, q4: true };
  }
}

/**
 * Detect which preset matches the current quadrant visibility
 */
export function detectQuadrantPreset(visibility: QuadrantVisibility): QuadrantPreset {
  const { q1, q2, q3, q4 } = visibility;
  
  if (q1 && q2 && q3 && q4) return 'all';
  if (q1 && !q2 && !q3 && !q4) return 'q1-only';
  if (q1 && !q2 && !q3 && q4) return 'q1-q4';
  if (q1 && q2 && !q3 && !q4) return 'q1-q2';
  return 'custom';
}

/**
 * Grid density to interval mapping
 */
export function getGridInterval(density: GridDensity): number {
  switch (density) {
    case 'coarse':
      return 1;
    case 'normal':
      return 0.5;
    case 'fine':
      return 0.25;
    default:
      return 0.5;
  }
}

/**
 * Get minor grid interval (half of main interval)
 */
export function getMinorGridInterval(density: GridDensity): number {
  return getGridInterval(density) / 2;
}

// ============================================================================
// EQUATION TYPES (Phase 2)
// ============================================================================

/**
 * Equation type classification
 */
export type EquationType = 'linear' | 'quadratic' | 'vertical' | 'horizontal';

/**
 * Parsed equation coefficients
 */
export interface EquationCoefficients {
  /** Gradient for linear equations (y = mx + c) */
  m?: number;
  /** Y-intercept for linear, or 'c' coefficient for quadratic */
  c?: number;
  /** Quadratic coefficient (y = ax² + bx + c) */
  a?: number;
  /** Linear coefficient for quadratic equations */
  b?: number;
  /** X-value for vertical lines (x = k) */
  k?: number;
}

/**
 * Result of parsing an equation string
 */
export interface ParsedEquation {
  type: EquationType;
  coefficients: EquationCoefficients;
  expression: string;
  isValid: boolean;
  error?: string;
}

/**
 * Gradient triangle for visualizing slope
 */
export interface GradientTriangle {
  id: string;
  /** First point on the line */
  point1: Point2D;
  /** Second point on the line */
  point2: Point2D;
  /** Display color */
  color: string;
  /** Whether to show Δx and Δy labels */
  showLabels: boolean;
}

/**
 * Plotted equation on the coordinate grid
 */
export interface PlotEquation {
  id: string;
  /** Original expression string (e.g., "y = 2x + 3") */
  expression: string;
  /** Parsed equation type */
  type: EquationType;
  /** Parsed coefficients */
  coefficients: EquationCoefficients;
  /** Display color */
  color: string;
  /** Line thickness in pixels */
  thickness: number;
  /** Whether equation is visible */
  visible: boolean;
  /** Show equation label on graph */
  showLabel: boolean;
  /** Show x and y intercept points */
  showIntercepts: boolean;
  /** Show gradient (for linear equations) */
  showGradient: boolean;
  /** Gradient visualization triangles */
  gradientTriangles: GradientTriangle[];
  /** Selection state */
  isSelected: boolean;
}

/**
 * Intersection point between two equations
 */
export interface EquationIntersection {
  /** IDs of the two intersecting equations */
  equationIds: [string, string];
  /** Intersection point */
  point: Point2D;
  /** Whether to show this intersection */
  visible: boolean;
}

/**
 * Intercept point on an axis
 */
export interface Intercept {
  type: 'x' | 'y';
  value: number;
  point: Point2D;
}

// ============================================================================
// POINT TYPES (Phase 3)
// ============================================================================

/**
 * Visual style options for point markers
 */
export type PointStyle = 'dot' | 'cross' | 'circle' | 'square';

/**
 * Size options for point markers
 */
export type PointSize = 'small' | 'medium' | 'large';

/**
 * Position options for coordinate labels
 */
export type CoordinatePosition = 'auto' | 'top' | 'bottom' | 'left' | 'right';

/**
 * Plotted point on the coordinate grid
 */
export interface PlottedPoint {
  id: string;
  x: number;
  y: number;
  /** Optional label (e.g., "A", "P₁") */
  label?: string;
  /** Whether to display coordinates near the point */
  showCoordinates: boolean;
  /** Position of coordinate label */
  coordinatePosition: CoordinatePosition;
  /** Visual style of the point marker */
  style: PointStyle;
  /** Size of the point marker */
  size: PointSize;
  /** Display color */
  color: string;
  /** Selection state */
  isSelected: boolean;
}

// ============================================================================
// SHAPE TYPES (Phase 3)
// ============================================================================

/**
 * Geometric shape types
 */
export type ShapeType = 'rectangle' | 'triangle' | 'parallelogram' | 'polygon';

/**
 * Geometric shape on the coordinate grid
 */
export interface GridShape {
  id: string;
  type: ShapeType;
  /** Ordered vertices defining the shape */
  vertices: Point2D[];
  /** Stroke color */
  color: string;
  /** Fill color (optional) */
  fillColor?: string;
  /** Fill opacity (0-1) */
  fillOpacity: number;
  /** Show vertex labels (A, B, C...) */
  showVertexLabels: boolean;
  /** Show coordinate values at vertices */
  showCoordinates: boolean;
  /** Show side length measurements */
  showMeasurements: boolean;
  /** Show area value */
  showArea: boolean;
  /** Calculated area (cached) */
  calculatedArea?: number;
  /** Calculated perimeter (cached) */
  calculatedPerimeter?: number;
  /** Selection state */
  isSelected: boolean;
}

// ============================================================================
// COORDINATE TABLE TYPES (Phase 4)
// ============================================================================

/**
 * Coordinate table showing x/y value pairs
 */
export interface CoordinateTable {
  id: string;
  /** Linked equation ID (or null for independent table) */
  equationId: string | null;
  /** X values in the table */
  xValues: number[];
  /** Whether to show calculated Y values */
  showYValues: boolean;
  /** Whether to show the equation expression */
  showEquation: boolean;
  /** Position on canvas (screen coordinates) */
  position: Point2D;
  /** Whether table is collapsed */
  isCollapsed: boolean;
  /** Column width for consistent display */
  columnWidth: number;
}

// ============================================================================
// TRANSFORMATION TYPES (Phase 5)
// ============================================================================

/**
 * Types of geometric transformations
 */
export type TransformationType = 'translation' | 'reflection' | 'rotation' | 'enlargement';

/**
 * Reflection line configuration
 */
export type ReflectionLine = 
  | { preset: 'x-axis' | 'y-axis' | 'y=x' | 'y=-x' }
  | { custom: { m: number; c: number } };

/**
 * Transformation-specific parameters
 */
export type TransformParameters = 
  | { type: 'translation'; vector: Point2D }
  | { type: 'reflection'; line: ReflectionLine }
  | { type: 'rotation'; centre: Point2D; angle: number; direction: 'cw' | 'ccw' }
  | { type: 'enlargement'; centre: Point2D; scaleFactor: number };

/**
 * Geometric transformation
 */
export interface Transformation {
  id: string;
  type: TransformationType;
  /** IDs of shapes/points being transformed */
  targetIds: string[];
  /** Whether to keep original visible */
  showOriginal: boolean;
  /** Whether to show construction aids */
  showConstruction: boolean;
  /** Color for transformed image */
  imageColor: string;
  /** Transformation-specific parameters */
  parameters: TransformParameters;
  /** Preview mode (before confirming) */
  isPreview: boolean;
}

/**
 * Result of applying a transformation
 */
export interface TransformResult {
  /** Original vertex/point positions */
  original: Point2D[];
  /** Transformed positions */
  transformed: Point2D[];
}

/**
 * Construction aid for visualizing transformations
 */
export interface ConstructionAid {
  type: 'line' | 'arc' | 'arrow' | 'marker';
  /** SVG path data or coordinates */
  path?: string;
  /** Start point for lines/arrows */
  start?: Point2D;
  /** End point for lines/arrows */
  end?: Point2D;
  /** Center point for arcs */
  centre?: Point2D;
  /** Radius for arcs */
  radius?: number;
  /** Start angle for arcs (degrees) */
  startAngle?: number;
  /** End angle for arcs (degrees) */
  endAngle?: number;
  /** Color */
  color: string;
  /** Stroke style */
  strokeStyle: 'solid' | 'dashed' | 'dotted';
  /** Stroke width */
  strokeWidth: number;
}

// ============================================================================
// VECTOR TYPES (Phase 7)
// ============================================================================

/**
 * Vector arrow on the coordinate grid
 */
export interface Vector {
  id: string;
  /** Tail position */
  start: Point2D;
  /** Head position (arrow end) */
  end: Point2D;
  /** Optional label (e.g., "a", "→AB") */
  label?: string;
  /** Show horizontal/vertical components */
  showComponents: boolean;
  /** Show magnitude value */
  showMagnitude: boolean;
  /** Display color */
  color: string;
  /** Arrow thickness */
  thickness: number;
  /** Selection state */
  isSelected: boolean;
}

/**
 * Vector sum showing resultant of multiple vectors
 */
export interface VectorSum {
  id: string;
  /** IDs of vectors being summed */
  vectorIds: string[];
  /** Calculated resultant */
  resultant: { start: Point2D; end: Point2D };
  /** Show individual vectors */
  showComponents: boolean;
  /** Color for resultant */
  resultantColor: string;
}

// ============================================================================
// INEQUALITY TYPES (Phase 7)
// ============================================================================

/**
 * Inequality operator types
 */
export type InequalityType = '<' | '>' | '<=' | '>=';

/**
 * Inequality region on the coordinate grid
 */
export interface Inequality {
  id: string;
  /** Original expression (e.g., "y > 2x + 1") */
  expression: string;
  /** Inequality operator */
  type: InequalityType;
  /** Boundary line equation */
  boundaryEquation: PlotEquation;
  /** Fill color for shaded region */
  fillColor: string;
  /** Fill opacity */
  fillOpacity: number;
  /** Boundary line style (solid for ≤/≥, dashed for </>) */
  boundaryStyle: 'solid' | 'dashed';
  /** Visibility */
  visible: boolean;
  /** Selection state */
  isSelected: boolean;
}

/**
 * Feasible region (intersection of multiple inequalities)
 */
export interface FeasibleRegion {
  /** IDs of inequalities defining the region */
  inequalityIds: string[];
  /** Highlight color for intersection */
  highlightColor: string;
}
